import React, { useEffect, useState, useContext } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTheme, alpha } from "@mui/material/styles";
import {
  Typography,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
  Box,
  Stack,
  Chip,
  useMediaQuery,
} from "@mui/material";
import AccountBalanceWalletOutlinedIcon from "@mui/icons-material/AccountBalanceWalletOutlined";
import TrendingUpOutlinedIcon from "@mui/icons-material/TrendingUpOutlined";
import TrendingDownOutlinedIcon from "@mui/icons-material/TrendingDownOutlined";
import SwipeLeftOutlinedIcon from "@mui/icons-material/SwipeLeftOutlined";

import { jwtDecode } from "jwt-decode";
import dayjs from "dayjs";

import { CashFlowApi } from "../../api/CashFlow/CashFlowApi";
import { SidebarContext } from "../Sidebar/SidebarProvider";
import {
  pageHeroShellSx,
  pageContentCardSx,
  pageFieldSx,
} from "../../styles/pageModernStyles";

import CashFlowTable from "./CashFlowTable";
import CashFlowChart from "./CashFlowChart";
import IVAChart from "./IVAChart";
import ProyeccionesChart from "./ProyeccionesChart";
import ClientesTable from "./ClientesTable";
import ProveedoresTable from "./ProveedoresTable";
import CashFlowMobile from "./CashFlowMobile";
import {
  buildMapByCliente,
  buildMapByProveedor,
  extractClienteDays,
  extractProveedorDays,
  getYearsForVencidoQuery,
  mergeMapByEntity,
  calcImporteVencidoPorAnio,
} from "./cashFlowUtils";

const CashFlow = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const navigate = useNavigate();
  const { contentMargin } = useContext(SidebarContext);
  const accent = theme.palette.primary.main;
  const pageGradient =
    theme.palette.mode === "dark"
      ? `linear-gradient(180deg, ${theme.palette.background.default} 0%, ${alpha(theme.palette.primary.main, 0.08)} 100%)`
      : "linear-gradient(180deg, #f8fafc 0%, #eef6f8 45%, #f5f3ff 100%)";

  const { empresa: empresaParam } = useParams();
  const empresa = (empresaParam || "stage").toLowerCase();

  const tituloMap = {
    stage: "Cash Flow · Stage",
    erna: "Cash Flow · ERNA",
    fusion: "Cash Flow · Fusionado",
  };

  const titulo = tituloMap[empresa] || "Cash Flow";

  const EMPRESA_OPTIONS = [
    { value: "stage", label: "Stage" },
    { value: "erna", label: "ERNA" },
    { value: "fusion", label: "Fusión" },
  ];

  const onEmpresaChange = (nextEmpresa) => {
    const target =
      nextEmpresa === "stage"
        ? "/cashflow"
        : `/cashflow/${encodeURIComponent(nextEmpresa)}`;
    navigate(target);
  };

  const [data, setData] = useState([]);
  const [days, setDays] = useState([]);
  const [year, setYear] = useState(new Date().getFullYear());

  const [mapByCliente, setMapByCliente] = useState({});
  const [clienteDays, setClienteDays] = useState([]);

  const [mapByProveedor, setMapByProveedor] = useState({});
  const [proveedorDays, setProveedorDays] = useState([]);

  const [importeVencidoClientes, setImporteVencidoClientes] = useState([]);
  const [importeVencidoProveedores, setImporteVencidoProveedores] = useState([]);

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) return navigate("/login");

    try {
      const decoded = jwtDecode(token);
      if (decoded.exp < Date.now() / 1000) {
        localStorage.removeItem("token");
        navigate("/login");
      }
    } catch {
      navigate("/login");
    }
  }, [navigate]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const yearsNeeded = getYearsForVencidoQuery(year);
        const rawByYear = await Promise.all(
          yearsNeeded.map((y) => CashFlowApi.getDiario(empresa, y)),
        );
        const rawMap = Object.fromEntries(
          yearsNeeded.map((y, i) => [y, rawByYear[i] || []]),
        );
        const raw = rawMap[year] || [];

        if (!raw || raw.length === 0) {
          setData([]);
          setDays([]);
          setMapByCliente({});
          setClienteDays([]);
          setMapByProveedor({});
          setProveedorDays([]);
        } else {
          setData(raw);
          setMapByCliente(buildMapByCliente(raw));
          setClienteDays(extractClienteDays(raw));
          setMapByProveedor(buildMapByProveedor(raw));
          setProveedorDays(extractProveedorDays(raw));
          setDays(
            [...new Set(raw.map((r) => r.fecha))].sort((a, b) =>
              dayjs(a).diff(dayjs(b)),
            ),
          );
        }

        const mergedClienteMap = mergeMapByEntity(
          ...yearsNeeded.map((y) => buildMapByCliente(rawMap[y])),
        );
        const mergedProveedorMap = mergeMapByEntity(
          ...yearsNeeded.map((y) => buildMapByProveedor(rawMap[y])),
        );

        setImporteVencidoClientes(calcImporteVencidoPorAnio(mergedClienteMap));
        setImporteVencidoProveedores(
          calcImporteVencidoPorAnio(mergedProveedorMap, dayjs(), {
            absAmount: true,
          }),
        );
      } catch (err) {
        console.error("❌ Error cargando cashflow:", err);
      }
    };

    fetchData();
  }, [empresa, year]);

  const formatValue = (val) => {
    if (val === undefined || val === null || val === 0) return "";
    return Number(val).toLocaleString("es-ES", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  const totalDia = data.reduce(
    (acc, d) => acc + Number(d?.cashFlowDia || 0),
    0,
  );
  const totalAcum = data.length
    ? Number(data[data.length - 1]?.cashFlowAcum || 0)
    : 0;

  const filtersRow = (
    <Stack
      direction={{ xs: "column", sm: "row" }}
      spacing={1}
      sx={{ width: { xs: "100%", lg: "auto" }, flexShrink: 0 }}
    >
      <FormControl size="small" fullWidth sx={{ ...pageFieldSx(theme), minWidth: { sm: 150 } }}>
        <InputLabel>Empresa</InputLabel>
        <Select
          value={empresa}
          label="Empresa"
          onChange={(e) => onEmpresaChange(String(e.target.value))}
        >
          {EMPRESA_OPTIONS.map((opt) => (
            <MenuItem key={opt.value} value={opt.value}>
              {opt.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <FormControl size="small" fullWidth sx={{ ...pageFieldSx(theme), minWidth: { sm: 120 } }}>
        <InputLabel>Año</InputLabel>
        <Select
          value={year}
          label="Año"
          onChange={(e) => setYear(e.target.value)}
        >
          <MenuItem value={2025}>2025</MenuItem>
          <MenuItem value={2026}>2026</MenuItem>
          <MenuItem value={2027}>2027</MenuItem>
        </Select>
      </FormControl>
    </Stack>
  );

  return (
    <Box
      sx={{
        height: "100dvh",
        maxHeight: "100dvh",
        boxSizing: "border-box",
        overflow: "auto",
        WebkitOverflowScrolling: "touch",
        background: pageGradient,
        pt: { xs: 1, md: 1.25 },
        px: { xs: 1.25, sm: 2 },
        pr: { xs: 1.25, sm: 2, md: 3 },
        pb: { xs: 10, md: 2 },
        ...contentMargin,
        minWidth: 0,
        width: "100%",
      }}
    >
      <Box sx={{ ...pageHeroShellSx(theme, { sticky: !isMobile }), flexShrink: 0 }}>
        <Box sx={{ p: { xs: 1.25, sm: 1.75 } }}>
          <Stack
            direction={{ xs: "column", lg: "row" }}
            spacing={1.25}
            justifyContent="space-between"
            alignItems={{ lg: "flex-start" }}
          >
            <Stack direction="row" spacing={1.25} sx={{ minWidth: 0, flex: 1 }}>
              <Box
                sx={{
                  width: 44,
                  height: 44,
                  borderRadius: 2,
                  flexShrink: 0,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  bgcolor: alpha(accent, 0.14),
                  color: "primary.dark",
                  border: "1px solid",
                  borderColor: alpha(accent, 0.28),
                }}
              >
                <AccountBalanceWalletOutlinedIcon fontSize="small" />
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography
                  variant="overline"
                  color="text.secondary"
                  sx={{ lineHeight: 1.1, letterSpacing: 1.1, fontWeight: 700 }}
                >
                  FINANCE · CASH FLOW
                </Typography>
                <Typography variant="h6" fontWeight={800} sx={{ lineHeight: 1.12 }}>
                  {titulo}
                </Typography>
                {empresa === "fusion" ? (
                  <Typography variant="caption" color="text.secondary">
                    Consolidado Stage + ERNA
                  </Typography>
                ) : null}
                <Stack
                  direction="row"
                  spacing={0.75}
                  useFlexGap
                  sx={{ mt: 0.75, flexWrap: "wrap" }}
                >
                  <Chip
                    size="small"
                    icon={<AccountBalanceWalletOutlinedIcon sx={{ fontSize: 16 }} />}
                    label={`Año ${year}`}
                    sx={{
                      fontWeight: 700,
                      bgcolor: theme.palette.background.paper,
                      border: "1px solid",
                      borderColor: theme.palette.divider,
                    }}
                  />
                  <Chip
                    size="small"
                    icon={
                      totalDia >= 0 ? (
                        <TrendingUpOutlinedIcon sx={{ fontSize: 16 }} />
                      ) : (
                        <TrendingDownOutlinedIcon sx={{ fontSize: 16 }} />
                      )
                    }
                    label={`CF año: ${formatValue(totalDia) || "0,00"}`}
                    sx={{
                      fontWeight: 800,
                      color: totalDia >= 0 ? "success.main" : "error.main",
                      bgcolor: theme.palette.background.paper,
                      border: "1px solid",
                      borderColor: theme.palette.divider,
                    }}
                  />
                  <Chip
                    size="small"
                    label={`CF acumulado: ${formatValue(totalAcum) || "0,00"}`}
                    sx={{
                      fontWeight: 800,
                      bgcolor: alpha(theme.palette.info.main, 0.12),
                      color: "info.dark",
                    }}
                  />
                </Stack>
              </Box>
            </Stack>
            {filtersRow}
          </Stack>
        </Box>
      </Box>

      {isMobile ? (
        <CashFlowMobile
          theme={theme}
          data={data}
          days={days}
          formatValue={formatValue}
          mapByCliente={mapByCliente}
          clienteDays={clienteDays}
          mapByProveedor={mapByProveedor}
          proveedorDays={proveedorDays}
          year={year}
          importeVencidoClientes={importeVencidoClientes}
          importeVencidoProveedores={importeVencidoProveedores}
        />
      ) : (
        <Stack spacing={2} sx={{ flexShrink: 0 }}>
          <Box
            sx={{
              ...pageContentCardSx(theme, accent),
              p: 0,
              overflow: "hidden",
            }}
          >
            <Box
              sx={{
                px: 2,
                py: 1,
                display: "flex",
                alignItems: "center",
                gap: 1,
                borderBottom: "1px solid",
                borderColor: "divider",
                bgcolor: alpha(theme.palette.primary.main, 0.03),
              }}
            >
              <SwipeLeftOutlinedIcon sx={{ fontSize: 18, color: "text.secondary" }} />
              <Typography variant="caption" color="text.secondary" fontWeight={600}>
                Matriz diaria · desplaza horizontalmente para ver todas las fechas
              </Typography>
            </Box>
            <Box sx={{ overflow: "auto", maxHeight: "72vh" }}>
              <Box sx={{ minWidth: Math.max(days.length * 110, 720) }}>
                <CashFlowTable
                  theme={theme}
                  days={days}
                  data={data}
                  formatValue={formatValue}
                />
              </Box>
            </Box>
          </Box>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
              gap: 2,
            }}
          >
            <CashFlowChart theme={theme} data={data} />
            <IVAChart data={data} />
            <ProyeccionesChart data={data} />
          </Box>

          <ClientesTable
            theme={theme}
            mapByCliente={mapByCliente}
            clienteDays={clienteDays}
            formatValue={formatValue}
            year={year}
            importeVencido={importeVencidoClientes}
          />

          <ProveedoresTable
            theme={theme}
            mapByProveedor={mapByProveedor}
            proveedorDays={proveedorDays}
            formatValue={formatValue}
            year={year}
            importeVencido={importeVencidoProveedores}
          />
        </Stack>
      )}
    </Box>
  );
};

export default CashFlow;
